import { useCallback, useEffect, useRef, useState } from 'react';

type ResumeEditorProps = {
  htmlTemplate: string;
  getHtmlContent?: (html: string) => void;
  /** Called (debounced) when the user types / edits inside the editor. */
  onSoftSave?: (html: string) => void;
  /** Debounce window (ms) before triggering onSoftSave. Default 1500ms. */
  softSaveDelay?: number;
};

type FormatState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  fontSize: string; // e.g. "12"
};

const FONT_SIZES = [
  '8',
  '9',
  '10',
  '11',
  '12',
  '14',
  '16',
  '18',
  '20',
  '24',
  '28',
  '36'
];

// ─────────────────────────────────────────────────────────────────────────────
//  Apply a specific font size (in pt) to the current selection inside the
//  iframe document.  Uses the Selection API to wrap selected content in a
//  <span style="font-size: Xpt">.
// ─────────────────────────────────────────────────────────────────────────────
const applyFontSize = (doc: Document, sizePt: string) => {
  const sel = doc.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  // If nothing selected, just move on — font size applies on next typed char
  // via styleWithCSS + fontSize execCommand fallback
  if (sel.isCollapsed) {
    doc.execCommand('styleWithCSS', false, 'true');
    // Map pt → HTML fontSize 1-7 (rough approximation used as fallback)
    const pt = parseInt(sizePt);
    const htmlSize =
      pt <= 8
        ? '1'
        : pt <= 10
          ? '2'
          : pt <= 12
            ? '3'
            : pt <= 14
              ? '4'
              : pt <= 18
                ? '5'
                : pt <= 24
                  ? '6'
                  : '7';
    doc.execCommand('fontSize', false, htmlSize);
    return;
  }

  const range = sel.getRangeAt(0);
  const span = doc.createElement('span');
  span.style.fontSize = `${sizePt}pt`;

  try {
    // Simple path: selection is within a single text node / element
    range.surroundContents(span);
  } catch {
    // Complex path: selection crosses element boundaries — extract & re-wrap
    const fragment = range.extractContents();
    span.appendChild(fragment);
    range.insertNode(span);
  }

  // Re-select the newly wrapped content so the user can continue formatting
  const newRange = doc.createRange();
  newRange.selectNodeContents(span);
  sel.removeAllRanges();
  sel.addRange(newRange);
};

// ─────────────────────────────────────────────────────────────────────────────
//  Detect the computed font size (in pt) at the current cursor / focus node
// ─────────────────────────────────────────────────────────────────────────────
const getActiveFontSize = (doc: Document): string => {
  const sel = doc.getSelection();
  if (!sel) return '12';
  const node = sel.focusNode;
  const el: Element | null =
    node?.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  if (!el) return '12';
  const iframeWin = doc.defaultView;
  if (!iframeWin) return '12';
  const pxStr = iframeWin.getComputedStyle(el).fontSize; // "16px"
  const px = parseFloat(pxStr);
  if (isNaN(px)) return '12';
  // Convert px → pt  (1pt = 1.333...px)
  const pt = Math.round(px * 0.75);
  // Snap to the nearest value in FONT_SIZES
  return FONT_SIZES.reduce((prev, cur) =>
    Math.abs(parseInt(cur) - pt) < Math.abs(parseInt(prev) - pt) ? cur : prev
  );
};

const ResumeEditor = ({
  htmlTemplate,
  getHtmlContent,
  onSoftSave,
  softSaveDelay = 1500
}: ResumeEditorProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const softSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep the latest onSoftSave in a ref so the iframe listener always sees the
  // newest callback without needing to re-attach on every render.
  const onSoftSaveRef = useRef(onSoftSave);
  const [fmt, setFmt] = useState<FormatState>({
    bold: false,
    italic: false,
    underline: false,
    fontSize: '12'
  });

  useEffect(() => {
    onSoftSaveRef.current = onSoftSave;
  }, [onSoftSave]);

  // Cleanup any pending soft-save on unmount
  useEffect(() => {
    return () => {
      if (softSaveTimerRef.current) clearTimeout(softSaveTimerRef.current);
    };
  }, []);

  // ── Enable editing + wire up toolbar state sync ───────────────────────────
  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    doc.designMode = 'on';

    const syncState = () => {
      setFmt({
        bold: doc.queryCommandState('bold'),
        italic: doc.queryCommandState('italic'),
        underline: doc.queryCommandState('underline'),
        fontSize: getActiveFontSize(doc)
      });
    };

    // ── Debounced soft save on user edits ──────────────────────────────────
    const triggerSoftSave = () => {
      if (!onSoftSaveRef.current) return;
      if (softSaveTimerRef.current) clearTimeout(softSaveTimerRef.current);
      softSaveTimerRef.current = setTimeout(() => {
        const html = doc.documentElement?.outerHTML ?? '';
        onSoftSaveRef.current?.(html);
      }, softSaveDelay);
    };

    doc.addEventListener('selectionchange', syncState);
    doc.addEventListener('keyup', syncState);
    // 'input' fires on typing, paste, cut, execCommand formatting, etc.
    doc.addEventListener('input', triggerSoftSave);
  }, [softSaveDelay]);

  // ── execCommand shortcut
  const exec = useCallback((cmd: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.execCommand(cmd, false);
    (iframeRef.current?.contentWindow as any)?.focus();
  }, []);

  // ── Font size handlers
  const changeFontSize = useCallback((newSize: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    applyFontSize(doc, newSize);
    setFmt(prev => ({ ...prev, fontSize: newSize }));
    (iframeRef.current?.contentWindow as any)?.focus();
  }, []);

  const stepFontSize = useCallback(
    (direction: 'up' | 'down') => {
      const idx = FONT_SIZES.indexOf(fmt.fontSize);
      const nextIdx =
        direction === 'up'
          ? Math.min(idx + 1, FONT_SIZES.length - 1)
          : Math.max(idx - 1, 0);
      if (nextIdx !== idx) changeFontSize(FONT_SIZES[nextIdx]);
    },
    [fmt.fontSize, changeFontSize]
  );

  // ── Save
  const getHTML = () =>
    iframeRef.current?.contentDocument?.documentElement?.outerHTML ?? '';

  // ── Toolbar button
  const Btn = ({
    label,
    onClick,
    active = false,
    title,
    extraClass = ''
  }: {
    label: string;
    onClick: () => void;
    active?: boolean;
    title?: string;
    extraClass?: string;
  }) => (
    <button
      title={title}
      onMouseDown={e => {
        e.preventDefault();
        onClick();
      }}
      className={`rounded border px-2 py-1 text-sm ${extraClass} ${
        active
          ? 'border-blue-400 bg-blue-50 font-semibold text-blue-700'
          : 'hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  const Divider = () => (
    <span className='mx-1 self-stretch border-l border-gray-300' />
  );

  useEffect(() => {
    if (getHtmlContent) getHtmlContent(getHTML());
  }, [getHtmlContent]);

  return (
    <div className='editor-container flex flex-col gap-4'>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className='flex flex-wrap items-center gap-1 border-b bg-gray-50 px-2 py-1.5'>
        {/* Text formatting */}
        <Btn
          label='B'
          onClick={() => exec('bold')}
          active={fmt.bold}
          title='Bold (Ctrl+B)'
          extraClass='font-bold w-8 text-center'
        />
        <Btn
          label='I'
          onClick={() => exec('italic')}
          active={fmt.italic}
          title='Italic (Ctrl+I)'
          extraClass='italic w-8 text-center'
        />
        <Btn
          label='U'
          onClick={() => exec('underline')}
          active={fmt.underline}
          title='Underline (Ctrl+U)'
          extraClass='underline w-8 text-center'
        />

        <Divider />

        {/* Font size — decrease / select / increase */}
        <button
          title='Decrease font size'
          onMouseDown={e => {
            e.preventDefault();
            stepFontSize('down');
          }}
          className='flex h-7 w-7 items-center justify-center rounded border text-base hover:bg-gray-100'
        >
          A<sub className='text-[9px] leading-none'>-</sub>
        </button>

        <select
          title='Font size'
          value={fmt.fontSize}
          onChange={e => changeFontSize(e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          className='h-7 w-16 rounded border bg-white px-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400'
        >
          {FONT_SIZES.map(s => (
            <option key={s} value={s}>
              {s} pt
            </option>
          ))}
        </select>

        <button
          title='Increase font size'
          onMouseDown={e => {
            e.preventDefault();
            stepFontSize('up');
          }}
          className='flex h-7 w-7 items-center justify-center rounded border text-base hover:bg-gray-100'
        >
          A<sup className='text-[9px] leading-none'>+</sup>
        </button>

        <Divider />

        {/* Lists */}
        <Btn
          label='≡ Bullets'
          onClick={() => exec('insertUnorderedList')}
          title='Bullet list'
        />
        <Btn
          label='≡ Numbers'
          onClick={() => exec('insertOrderedList')}
          title='Numbered list'
        />

        <Divider />

        {/* History */}
        <Btn
          label='↩'
          onClick={() => exec('undo')}
          title='Undo (Ctrl+Z)'
          extraClass='w-8 text-center'
        />
        <Btn
          label='↪'
          onClick={() => exec('redo')}
          title='Redo (Ctrl+Y)'
          extraClass='w-8 text-center'
        />
      </div>

      {/* ── Resume surface (iframe) ──────────────────────────────────────── */}
      <div className='mx-auto shadow-xl' style={{ width: '8.5in' }}>
        <iframe
          ref={iframeRef}
          srcDoc={htmlTemplate}
          title='Resume Editor'
          id='resume-iframe'
          onLoad={handleLoad}
          style={{
            width: '8.5in',
            minHeight: '11in',
            border: 'none',
            display: 'block',
            background: '#fff'
          }}
          sandbox='allow-same-origin allow-scripts'
        />
      </div>
    </div>
  );
};

export default ResumeEditor;
